/**
 *
 * @module deviceClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/device.md
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/device.md
 *
 */

/* eslint-disable camelcase */

import Check from 'check-types';
import Logging from './../../logging/entry';
import Exception from './../exception/entry';
import Token from './../token/entry';
import HttpClient from './../platforms/httpClient';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import DeviceClientConfiguration from './deviceClientConfiguration';
import DeviceMetadata from './deviceMetadata';
import DeviceClientEndpoint from './deviceClientEndpoint';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const DeviceGrant = Token.DeviceGrant;
const HttpMethod = Configuration.HttpMethod;
const DeviceClientDustReference = DustUrnReference.services.device.deviceClient;

const {
    nonEmptyString,
    instanceStrict
} = Check.assert;

/**
 *
 * @class DeviceClient
 * @implements {IDeviceClient}
 * @desc Provides a data client that can be used to access device services.
 *
 */
export default class DeviceClient {

    /**
     *
     * @param {DeviceClientConfiguration} deviceClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(deviceClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'DeviceClient(deviceClientConfiguration, logger, httpClient)',
            get deviceClientConfiguration() {
                return `${this.ctor} needs DeviceClientConfiguration instance`;
            }
        };

        instanceStrict(
            deviceClientConfiguration,
            DeviceClientConfiguration,
            errorMessage.deviceClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {DeviceClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = deviceClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(DeviceClient.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {DeviceMetadata} deviceMetadata - The device metadata.
     * @param {String} apiKey - The API key to provide security.
     * @desc Creates a device grant for the provided device criteria.
     * @returns {Promise<SDK.Services.Token.DeviceGrant>} A device grant if successful,
     * otherwise a temporary grant indicating device grant creation failed due to internal failure.
     *
     */
    createDeviceGrant(deviceMetadata, apiKey) {

        const errorMessage = {
            method: 'DeviceClient.createDeviceGrant(deviceMetadata, apiKey)',
            get deviceMetadata() {
                return `${this.method} needs DeviceMetadata instance`;
            },
            get apiKey() {
                return `${this.method} apiKey should be a String with length`;
            }
        };

        instanceStrict(deviceMetadata, DeviceMetadata, errorMessage.deviceMetadata, InvalidArgumentException);
        nonEmptyString(apiKey, errorMessage.apiKey, InvalidArgumentException);

        const { dustEnabled, httpClient, logger } = this;
        const { deviceFamily, applicationRuntime, deviceProfile, extraData } = deviceMetadata;

        const payload = this.getPayload(
            apiKey,
            DeviceClientEndpoint.createDeviceGrant,
            {
                deviceFamily,
                applicationRuntime,
                deviceProfile,
                attributes: extraData
            }
        );

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DeviceClient.name,
            urn: DeviceClientDustReference.createDeviceGrant,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { grant_type, assertion } = data;

            return Promise.resolve(
                new DeviceGrant(grant_type, assertion)
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} apiKey
     * @param {String} rel
     * @param {Object} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(apiKey, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{apiKey\}/gi, apiKey);

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/device/deviceClient.js